import classNames from 'classnames'
import React, { useContext, useState } from 'react'
import { Trans, t } from '@lingui/macro'

import { SurveyQuestionTypes } from '../../../../../../shared/surveys'
import AggregatedResult from '../../../../components/common/aggregated-results/AggregatedResult'
import { getFormattedOptionStats } from '../../helpers'
import { SurveyResultsContext } from './SurveyResults'
import { SurveyResultsDetailModal } from './SurveyResultsDetailModal'
import { LinkableUserAvatar } from '../../../../components/common/user/linkable-user-avatar/LinkableUserAvatar'
import { SurveyQuestionWithAnswersFragment } from './SurveyResults.codegen'
import { Box, ButtonOutline } from 'front-end/src/components'

import './SurveyResultsQuestions.less'

const DEFAULT_VISIBLE_ANSWERS_COUNT = 10
const MAX_VISIBLE_ANSWERS = 500

interface IRenderResultDetail {
  question: SurveyQuestionWithAnswersFragment
}
const SurveyResultsQuestions = () => {
  const { questionsWithResult } = useContext(SurveyResultsContext)
  const [maxTextAnswers, setMaxTextAnswers] = useState(
    DEFAULT_VISIBLE_ANSWERS_COUNT
  )

  const RenderResultDetail = ({ question }: IRenderResultDetail) => {
    const [showResultDetailModal, setShowResultDetailModal] =
      useState<boolean>(false)
    return (
      <Box>
        <ButtonOutline
          mt="8px"
          onClick={() => {
            setShowResultDetailModal(true)
          }}
        >
          <Trans>Show more details</Trans>
        </ButtonOutline>
        {showResultDetailModal && (
          <SurveyResultsDetailModal
            question={question}
            onHide={() => setShowResultDetailModal(false)}
          />
        )}
      </Box>
    )
  }

  const countAvg = (question) => {
    if (question.type === SurveyQuestionTypes.RATING) {
      const totalCount = question.aggregatedAnswers.reduce((acc, option) => {
        return (
          acc +
          option.count *
            ([...question.content.options]
              .reverse()
              .findIndex((o) => o.id === option.value) +
              1)
        )
      }, 0)
      return parseFloat((totalCount / question.answersCount).toFixed(1))
    }
    return null
  }

  const countMedian = (question) => {
    if (question.type === SurveyQuestionTypes.RATING) {
      let values: number[] = []

      for (let item of question.aggregatedAnswers) {
        for (let i = 0; i < item.count; i++) {
          values.push(
            [...question.content.options]
              .reverse()
              .findIndex((opt) => opt.id === item.value) + 1
          )
        }
      }
      const half = Math.floor(values.length / 2)
      const nums = [...values].sort((a, b) => a - b)

      if (values.length % 2) {
        return nums[half]
      }

      return (nums[half - 1] + nums[half]) / 2
    }
    return null
  }

  const renderOptionsStats = (question: SurveyQuestionWithAnswersFragment) => {
    const formattedOptions = getFormattedOptionStats(question)
    return (
      <AggregatedResult
        total={question.answersCount}
        items={formattedOptions}
        avg={countAvg(question)}
        median={countMedian(question)}
      />
    )
  }

  const renderTextStats = (question) => {
    return (
      <>
        <div className="SurveyResultsQuestion__responses">
          <Trans>Responses</Trans>: <strong>{question.answersCount}</strong>
        </div>
        {question.aggregatedAnswers.map((option, index) => {
          if (index > maxTextAnswers) {
            return null
          }
          return (
            <div
              className={classNames('SurveyResultsQuestion__textItem', {
                'SurveyResultsQuestion__textItem--shadowed':
                  index >= maxTextAnswers
              })}
              key={`u-${option.user.id}`}
            >
              <div className="SurveyResultsQuestion__author">
                <LinkableUserAvatar size="small" user={option.user} />
                {option.user.first_name} {option.user.last_name}
              </div>
              <div
                className="SurveyResultsQuestion__quote"
                key={`${option.first_name}-${option.last_name}-${option.value}`}
              >
                "{option.value}"
              </div>
            </div>
          )
        })}
        {question.aggregatedAnswers.length > DEFAULT_VISIBLE_ANSWERS_COUNT && (
          <div className="SurveyResultsQuestion__showMore">
            <ButtonOutline
              onClick={() => {
                if (maxTextAnswers === DEFAULT_VISIBLE_ANSWERS_COUNT) {
                  setMaxTextAnswers(MAX_VISIBLE_ANSWERS)
                } else {
                  setMaxTextAnswers(DEFAULT_VISIBLE_ANSWERS_COUNT)
                }
              }}
            >
              <Trans>Show</Trans>{' '}
              {maxTextAnswers === DEFAULT_VISIBLE_ANSWERS_COUNT
                ? t`more`
                : t`less`}
            </ButtonOutline>
          </div>
        )}
      </>
    )
  }

  return (
    <>
      {questionsWithResult.map((question, index) => {
        return (
          <div className={`SurveyResultsQuestion`} key={question.id}>
            <div className="SurveyResultsQuestion__top">
              <span className="SurveyResultsQuestion__order">
                <Trans>Question {index + 1}</Trans>
              </span>
            </div>

            <div
              className={`SurveyResultsQuestion__content SurveyResultsQuestion__content--${question.type}`}
            >
              <div className="SurveyResultsQuestion__title">
                <h2>{question.title}</h2>
                {question.type !== SurveyQuestionTypes.TEXT && (
                  <RenderResultDetail question={question} />
                )}
              </div>

              <div className="SurveyResultsQuestion__options">
                {question.aggregatedAnswers.length === 0 ? (
                  <div className="SurveyResultsQuestion__noResult">
                    <Trans>No results</Trans>
                  </div>
                ) : question.type === SurveyQuestionTypes.TEXT ? (
                  renderTextStats(question)
                ) : (
                  renderOptionsStats(question)
                )}
              </div>
            </div>
          </div>
        )
      })}
    </>
  )
}

export default SurveyResultsQuestions
